// client/src/components/secure/ZoningLookupBox.js — "Look up an address".
//
// Type an address: the server finds it, reads the PIN and town from the county parcel
// data and the district from the town's online zoning map (lib/zoningLookup.js), then
// answers from the Zoning Library for the chosen use. A town with no online map is queued
// for the office AI runner; staff can also type the district themselves. An AI-read
// district shows "— confirm" until somebody presses Confirm.
//
// Used on the Zoning Library page and in a client's site search (where "Add to this
// search" files the address as a property).
import React, { useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';

export const LABEL_TONE = {
    by_right: 'bg-green-100 text-green-800 border-green-200',
    special_use: 'bg-amber-100 text-amber-800 border-amber-200',
    not_allowed: 'bg-red-100 text-red-800 border-red-200',
    unverified: 'bg-gray-100 text-gray-700 border-gray-200',
};
export const LABEL_TEXT = {
    by_right: 'BY RIGHT', special_use: 'SPECIAL USE', not_allowed: 'NOT ALLOWED', unverified: 'UNVERIFIED',
};

const Fact = ({ k, children }) => (
    <div><dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{k}</dt>
        <dd className="text-sm text-gray-900">{children}</dd></div>
);

export const ManualDistrictForm = ({ lookupId, useCategory, onSaved, initial = '' }) => {
    const [district, setDistrict] = useState(initial);
    const [source, setSource] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const apiClient = useApiClient();
    const save = async (e) => {
        e.preventDefault();
        setBusy(true); setErr('');
        try {
            onSaved(await apiClient.post(`/api/site-searches/zoning-lookup/${lookupId}/district`,
                { district, source, use_category: useCategory || null }));
        } catch (ex) { setErr(ex.message); } finally { setBusy(false); }
    };
    return (
        <form onSubmit={save} className="mt-2 flex flex-wrap items-end gap-2 rounded border border-gray-200 bg-gray-50 p-2 text-xs">
            <label>District<input required value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="B-1"
                className="mt-1 block w-24 rounded border border-gray-300 px-2 py-1" /></label>
            <label className="flex-1">Where it came from<input required value={source} onChange={(e) => setSource(e.target.value)}
                placeholder="Vernon Hills zoning map PDF, 2024" className="mt-1 block w-full rounded border border-gray-300 px-2 py-1" /></label>
            <button disabled={busy} className="rounded bg-gray-800 px-3 py-1.5 font-semibold text-white disabled:opacity-50">
                {busy ? 'Saving…' : 'Save district'}</button>
            {err && <p className="w-full text-red-700">{err}</p>}
        </form>
    );
};

export const LookupResult = ({ r, onChange, searchId, onAdd, adding }) => {
    const [manual, setManual] = useState(false);
    const [busy, setBusy] = useState(false);
    const apiClient = useApiClient();
    const lk = r.lookup;
    const v = r.verdict || {};
    const confirm = async () => {
        setBusy(true);
        try { onChange(await apiClient.post(`/api/site-searches/zoning-lookup/${lk.lookup_id}/confirm`, { use_category: r.use_category })); }
        catch (ex) { alert(ex.message); } finally { setBusy(false); }
    };
    return (
        <div className="space-y-2 rounded border border-gray-200 bg-white p-3">
            <dl className="grid grid-cols-2 gap-2 md:grid-cols-4">
                <Fact k="Found as">{lk.matched_address || lk.address_input}
                    {lk.geocode_match && (
                        <span className={`block text-[11px] ${/^Street/.test(lk.geocode_match) ? 'font-semibold text-amber-700' : 'text-gray-500'}`}>
                            {/^Street/.test(lk.geocode_match) ? 'matched to the street, not the building — check the map'
                                : lk.geocode_match === 'PointAddress' ? 'matched to the building' : `match: ${lk.geocode_match}`}
                        </span>
                    )}
                </Fact>
                <Fact k="Town">{lk.municipality || '—'}{lk.county ? <span className="text-gray-500"> · {lk.county} County</span> : null}</Fact>
                <Fact k="PIN">{lk.pin || '—'}{lk.pin_note && <span className="block text-[11px] text-gray-500">{lk.pin_note}</span>}</Fact>
                <Fact k="District">
                    <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs">{lk.district || '—'}</span>
                    {v.source_label && (
                        <span className={`block text-[11px] ${v.unconfirmed ? 'font-semibold text-amber-700' : 'text-gray-500'}`}>{v.source_label}</span>
                    )}
                </Fact>
            </dl>

            {v.unconfirmed && (
                <div className="flex flex-wrap items-center gap-2 rounded border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    The office AI read this district from the town map. It does not count as verified until a person checks it.
                    <button onClick={confirm} disabled={busy} className="rounded bg-amber-600 px-2 py-1 font-semibold text-white disabled:opacity-50">Confirm district</button>
                    <button onClick={() => setManual(true)} className="rounded border border-amber-600 px-2 py-1 font-semibold">Correct it</button>
                </div>
            )}

            {!lk.district && (
                <div className="rounded border-l-4 border-gray-400 bg-gray-50 px-3 py-2 text-xs text-gray-700">
                    <p><strong>No district yet.</strong> {lk.reason === 'no online map'
                        ? `${lk.municipality || 'This town'} has no online zoning map that can be asked automatically.` : lk.reason}</p>
                    {r.queued_message && <p className="mt-1 font-semibold text-indigo-700">{r.queued_message}</p>}
                    {r.request && r.request.status === 'failed' && (
                        <p className="mt-1 text-red-700">The office AI could not read it: {r.request.notes || 'no reason given'}. Type the district below.</p>
                    )}
                </div>
            )}
            {lk.map_link && <a href={lk.map_link} target="_blank" rel="noreferrer" className="text-xs text-blue-700 hover:underline">Open the town's zoning map / website ↗</a>}
            {(!lk.district || manual) && (
                <div>
                    {!manual && <button onClick={() => setManual(true)} className="text-xs font-semibold text-indigo-700 hover:underline">Type the district myself</button>}
                    {manual && <ManualDistrictForm lookupId={lk.lookup_id} useCategory={r.use_category} initial={lk.district || ''}
                        onSaved={(x) => { setManual(false); onChange(x); }} />}
                </div>
            )}
            {lk.district && !manual && !v.unconfirmed && (
                <button onClick={() => setManual(true)} className="text-xs text-gray-500 hover:underline">Correct the district</button>
            )}

            {r.use_category ? (
                <div className="rounded border border-gray-200 p-2 text-sm">
                    <p className="flex flex-wrap items-center gap-2">
                        <span className="text-xs text-gray-500">{r.use_category}:</span>
                        <span className={`rounded border px-2 py-0.5 text-[10px] font-semibold ${LABEL_TONE[v.label] || LABEL_TONE.unverified}`}>{LABEL_TEXT[v.label] || 'UNVERIFIED'}</span>
                        {v.unconfirmed && <span className="text-[11px] font-semibold text-amber-700">unconfirmed district</span>}
                        {r.library && !v.library_verified && <span className="text-[11px] text-gray-500">library entry not verified</span>}
                    </p>
                    <p className="mt-1 text-xs text-gray-700">{v.reason}</p>
                    {v.verdict && <p className="mt-1 text-xs text-gray-700"><strong>Library:</strong> {v.verdict}</p>}
                    {v.citation && <p className="text-xs text-gray-600"><strong>Code:</strong> {v.citation}</p>}
                    {v.interpretation && <p className="whitespace-pre-line text-xs text-gray-600"><strong>Interpretation:</strong> {v.interpretation}</p>}
                    {v.source_url && <a href={v.source_url} target="_blank" rel="noreferrer" className="text-xs text-blue-700 hover:underline">Code source ↗</a>}
                </div>
            ) : <p className="text-xs text-gray-500">Choose a use to see what the Zoning Library says about it.</p>}

            {searchId && (
                <div className="flex justify-end">
                    <button onClick={onAdd} disabled={adding} className="rounded bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                        {adding ? 'Adding…' : 'Add to this search'}
                    </button>
                </div>
            )}
        </div>
    );
};

const ZoningLookupBox = ({ useCategory = '', uses = [], searchId = null, onAdded }) => {
    const [address, setAddress] = useState('');
    const [use, setUse] = useState(useCategory);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [adding, setAdding] = useState(false);
    const [source, setSource] = useState('other');
    const apiClient = useApiClient();

    const look = async (e, refresh = false) => {
        if (e) e.preventDefault();
        setBusy(true); setError(''); setResult(null);
        try {
            setResult(await apiClient.post('/api/site-searches/zoning-lookup', { address, use_category: use || null, refresh }));
        } catch (ex) { setError(ex.message || 'The lookup did not work.'); } finally { setBusy(false); }
    };

    const add = async () => {
        const lk = result.lookup;
        setAdding(true);
        try {
            const out = await apiClient.post(`/api/site-searches/${searchId}/candidates`, {
                address: lk.matched_address ? lk.matched_address.split(',')[0] : lk.address_input,
                municipality: lk.municipality || null, pin: lk.pin || null, source,
                zoning_lookup_id: lk.lookup_id,
            });
            alert(out.added ? 'Added to this search.' : 'This building is already in the search; its details were refreshed.');
            if (onAdded) onAdded();
        } catch (ex) { alert(ex.message); } finally { setAdding(false); }
    };

    return (
        <div className="space-y-2 rounded border border-indigo-100 bg-indigo-50/40 p-3">
            <form onSubmit={look} className="flex flex-wrap items-end gap-2 text-sm">
                <label className="min-w-[16rem] flex-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Look up an address
                    <input required value={address} onChange={(e) => setAddress(e.target.value)}
                        placeholder="1945 Techny Rd, Northbrook, IL"
                        className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm font-normal normal-case" /></label>
                {!useCategory && (
                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">For use
                        <select value={use} onChange={(e) => setUse(e.target.value)}
                            className="mt-1 block rounded border border-gray-300 px-2 py-2 text-sm font-normal normal-case">
                            <option value="">(any — no answer)</option>
                            {uses.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select></label>
                )}
                {searchId && (
                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Found on
                        <select value={source} onChange={(e) => setSource(e.target.value)}
                            className="mt-1 block rounded border border-gray-300 px-2 py-2 text-sm font-normal normal-case">
                            <option value="other">Other</option><option value="loopnet">LoopNet</option><option value="crexi">Crexi</option>
                            <option value="cityfeet">CityFeet</option><option value="commercialcafe">CommercialCafe</option>
                            <option value="commercialsearch">CommercialSearch</option>
                        </select></label>
                )}
                <button disabled={busy} className="rounded bg-slate-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                    {busy ? 'Looking…' : 'Look up'}</button>
                {result && <button type="button" onClick={() => look(null, true)} className="rounded border border-gray-300 px-3 py-2 text-xs">Ask the maps again</button>}
            </form>
            {error && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
            {result && <LookupResult r={result} onChange={setResult} searchId={searchId} onAdd={add} adding={adding} />}
            {result && result.lookup.cached && <p className="text-[11px] text-gray-500">From the saved answer of {new Date(result.lookup.looked_up_at || result.lookup.updated_at).toLocaleDateString()} — "Ask the maps again" refreshes it.</p>}
        </div>
    );
};

export default ZoningLookupBox;
